"use client";

import { forwardRef, ReactNode } from "react";
import { ActionIcon, Box as MantineBox, Text as MantineText, UnstyledButton } from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import styles from "./styles.module.scss";

export type SlideInSide = "left" | "right";
export type SlideInWidth = "narrow" | "medium" | "full";
export type SlideInOverflow = "hidden" | "auto";
export type SlideInPadding = "desktop" | "mobile" | "none";
export type SlideInFullWidthBreakpoint = "small" | "large";

export interface SlideInProps {
  /** Whether the slide-in is currently open/visible */
  active: boolean;
  /** Callback when close button is clicked */
  onClose: () => void;
  /** Which side to slide from */
  side?: SlideInSide;
  /** Width variant */
  width?: SlideInWidth;
  /** Breakpoint at which a medium slide-in becomes full width */
  fullWidthAt?: SlideInFullWidthBreakpoint;
  /** Whether to offset top by navbar height (85px) or use full height */
  offsetTop?: boolean;
  /** Overflow behavior */
  overflow?: SlideInOverflow;
  /** Padding variant */
  padding?: SlideInPadding;
  /** Whether to animate content on open */
  animate?: boolean;
  /** Optional title for the close header */
  title?: string | ReactNode;
  /** Optional actions to display next to the title */
  titleActions?: ReactNode;
  /** Custom header content (overrides title) */
  header?: ReactNode;
  /** Whether to hide the default close button */
  hideDefaultClose?: boolean;
  /** Main content */
  children: ReactNode;
  /** Optional className for the container */
  className?: string;
  /** Optional data attributes for styling */
  dataAttrs?: Record<string, string | boolean | undefined>;
  /** Optional id */
  id?: string;
  /** Whether to render a modal backdrop and dialog semantics */
  modal?: boolean;
  /** Accessible label for the modal backdrop close target */
  backdropLabel?: string;
  /** Accessible name for the modal dialog */
  modalLabel?: string;
  /** Prevent interaction while a higher layer is open */
  interactionDisabled?: boolean;
}

export const SlideIn = forwardRef<HTMLDivElement, SlideInProps>(
  (
    {
      active,
      onClose,
      side = "right",
      width = "narrow",
      fullWidthAt = "large",
      offsetTop = true,
      overflow = "auto",
      padding = "desktop",
      animate = true,
      title,
      titleActions,
      header,
      hideDefaultClose,
      children,
      className,
      dataAttrs,
      id,
      modal = false,
      backdropLabel = "Close panel",
      modalLabel,
      interactionDisabled = false,
    },
    ref
  ) => {
    const isHiddenFromInteraction = !active || interactionDisabled;
    const containerDataAttrs = {
      "data-active": active,
      "data-side": side,
      "data-width": width,
      "data-full-width-at": fullWidthAt,
      "data-offset": offsetTop,
      "data-overflow": overflow,
      "data-padding": padding,
      "data-animate": animate,
      "data-modal": modal,
      "data-interaction-disabled": interactionDisabled,
      ...dataAttrs,
    };

    const content =
      overflow === "auto" ? (
        <MantineBox className={styles.scrollContent}>{children}</MantineBox>
      ) : (
        <MantineBox className={styles.contentWrapper}>{children}</MantineBox>
      );

    return (
      <>
        {modal && active && (
          <UnstyledButton
            className={styles.backdrop}
            onClick={onClose}
            aria-label={backdropLabel}
            tabIndex={-1}
          />
        )}
        <MantineBox
          ref={ref}
          id={id}
          className={`${styles.slideIn} ${className || ""}`}
          data-scroll-nag-exclude="true"
          aria-hidden={isHiddenFromInteraction}
          aria-modal={modal && active ? true : undefined}
          aria-label={
            modal && active
              ? (modalLabel ?? (typeof title === "string" ? title : undefined))
              : undefined
          }
          role={modal && active ? "dialog" : undefined}
          inert={isHiddenFromInteraction ? true : undefined}
          {...containerDataAttrs}
        >
          {header ||
            (hideDefaultClose ? null : (
              <MantineBox className={styles.closeContainer}>
                {(title || titleActions) && (
                  <MantineBox className={styles.headerLeft}>
                    {title && (
                      <MantineText span className={styles.closeTitle}>
                        {title}
                      </MantineText>
                    )}
                    {titleActions}
                  </MantineBox>
                )}
                <ActionIcon
                  onClick={onClose}
                  variant="subtle"
                  radius="sm"
                  size={48}
                  className={styles.closeIcon}
                  aria-label="Close"
                  style={{ width: 48, minWidth: 48, height: 48, minHeight: 48 }}
                >
                  <IconX size={24} stroke={2} style={{ color: "black" }} />
                </ActionIcon>
              </MantineBox>
            ))}
          {content}
        </MantineBox>
      </>
    );
  }
);

SlideIn.displayName = "SlideIn";
